import React from 'react';
import { useGetChatListQuery } from '../../redux/apis/endpoints/messageApi';
import ChatListItem from './ChatListItem';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import LoadingSpinner from '../common/LoadingSpinner';

const ChatList = ({ onSelectChat, selectedChatId, productContextSellerInfo, className = '' }) => {
  const { data: chatListFromQuery, isLoading, error } = useGetChatListQuery();
  
  // --- DEBUG LOGS for ChatList ---
  console.log('[ChatList DEBUG] selectedChatId (prop):', selectedChatId, 'Type:', typeof selectedChatId);
  if (chatListFromQuery) {
    console.log('[ChatList DEBUG] chatList data entries (showing id/type details):', 
      chatListFromQuery.slice(0, 5).map(c => ({ 
        chat_id: c.id, 
        chat_id_type: typeof c.id, 
        chat_username: c.username 
        // Avoid logging the full object here to prevent excessive console noise
      }))
    );
  }
  // --- END DEBUG LOGS ---

  let displayChatList = chatListFromQuery ? [...chatListFromQuery] : [];
  let productSellerChatExistsInList = false;

  if (productContextSellerInfo && productContextSellerInfo.id) {
    if (displayChatList.some(chat => chat.id != null && selectedChatId != null && chat.id == productContextSellerInfo.id)) {
      productSellerChatExistsInList = true;
    }
    // If the product context seller is not in the main list and is the currently selected one,
    // or if the chatList is empty but we have product context, prepend it.
    if (!productSellerChatExistsInList && 
        (selectedChatId == productContextSellerInfo.id || displayChatList.length === 0)) {
      // Create a chat-like object for the product context seller to display in the list
      const placeholderChat = {
        ...productContextSellerInfo,
        lastMessage: "Chat regarding product", // Placeholder message
        lastMessageDate: new Date().toISOString(), // Or null if preferred
        isPlaceholder: true // Custom flag for ChatListItem if specific styling is needed
      };
      displayChatList.unshift(placeholderChat);
    }
  }
  
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <LoadingSpinner />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-6 ${className}`}>
        <ExclamationCircleIcon className="h-12 w-12 text-red-500 mb-4" />
        <h3 className="font-medium text-lg text-gray-800">Failed to load chats</h3>
        <p className="text-gray-500 text-center mt-2">{error.message || 'Please try again later'}</p>
      </div>
    );
  }
  
  if (!displayChatList || displayChatList.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center p-6 ${className}`}>
        <div className="bg-brand-teal bg-opacity-10 rounded-full p-3 mb-4">
          <svg className="h-10 w-10 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <h3 className="font-medium text-lg text-gray-800">No messages yet</h3>
        <p className="text-gray-500 text-center mt-2">
          Your conversations will appear here
        </p>
      </div>
    );
  }
  
  return (
    <div className={`flex flex-col h-full ${className}`}>
      <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
        <h2 className="text-lg font-medium text-gray-900">Conversations</h2>
      </div>
      
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {displayChatList.map(chat => (
          <ChatListItem
            key={chat.id}
            chat={chat}
            isActive={selectedChatId != null && chat.id != null && String(selectedChatId) == String(chat.id)}
            onClick={() => onSelectChat(chat)}
          />
        ))}
      </div>
    </div>
  );
};

export default ChatList; 